import axiosInstance from '../../axiosConfig';
import {
  ApiReportItem,
  CountdownParts,
  RegistrationState,
  RegistrationWindowFields,
  TeamRow,
} from './TeamRegistration.types';
import type { ReportListItem } from '../../store/reportStore';
import type { Team } from '../../store/teamRegistrationStore';
import type { AssociationInfo } from '../../store/associationStore';

export class TeamRegistrationService {
  static parseDottedDateToDate(value: string): Date | null {
    if (!value) return null;
    const trimmed = value.trim();
    // Match: d.m.Y [H:i[:s]] with optional extra tokens (e.g., "Uhr")
    const match = trimmed.match(
      /^(\d{1,2})\.(\d{1,2})\.(\d{4})(?:\s+(\d{1,2}):(\d{2})(?::(\d{2}))?)?/,
    );
    if (!match) return null;
    const day = Number(match[1]);
    const month = Number(match[2]);
    const year = Number(match[3]);
    const hours = match[4] !== undefined ? Number(match[4]) : 0;
    const minutes = match[5] !== undefined ? Number(match[5]) : 0;
    const seconds = match[6] !== undefined ? Number(match[6]) : 0;

    if (
      !Number.isFinite(day) ||
      !Number.isFinite(month) ||
      !Number.isFinite(year) ||
      !Number.isFinite(hours) ||
      !Number.isFinite(minutes) ||
      !Number.isFinite(seconds)
    ) {
      return null;
    }
    if (month < 1 || month > 12) return null;
    if (day < 1 || day > 31) return null;
    if (hours < 0 || hours > 23) return null;
    if (minutes < 0 || minutes > 59) return null;
    if (seconds < 0 || seconds > 59) return null;

    const date = new Date(year, month - 1, day, hours, minutes, seconds);
    return isNaN(date.getTime()) ? null : date;
  }

  static isFutureDate(date: Date | null): boolean {
    if (!date) return false;
    return date.getTime() - Date.now() > 0;
  }

  /**
   * Where a report sits in its registration window.
   *
   * The backend decides this and sends `registration_state`; parsing the dates
   * is only the fallback for a backend that has not been deployed yet.
   */
  static registrationState(item: RegistrationWindowFields): RegistrationState {
    if (
      item.registration_state === 'not_yet_open' ||
      item.registration_state === 'open' ||
      item.registration_state === 'closed'
    ) {
      return item.registration_state;
    }

    const deadlineDate = this.parseDottedDateToDate(item.deadline);
    if (!this.isFutureDate(deadlineDate)) return 'closed';

    const startDate = this.parseDottedDateToDate(item.date_start ?? '');
    if (this.isFutureDate(startDate)) return 'not_yet_open';

    return 'open';
  }

  /** The badge text for a state — the one place the wording lives. */
  static registrationStatusLabel(state: RegistrationState): string {
    if (state === 'open') return 'Registrierung offen';
    if (state === 'not_yet_open') return 'Noch nicht geöffnet';
    return 'Beendet';
  }

  static mapReportItems(items: ApiReportItem[]): TeamRow[] {
    return items.map((item) => {
      const state = this.registrationState(item);
      const status = this.registrationStatusLabel(state);
      return {
        id: item.id,
        title: item.title,
        deadline: item.deadline,
        invoice_date: item.invoice_date,
        date_start: item.date_start,
        date_end: item.date_end,
        registration_state: state,
        status,
        registrationLink:
          state === 'open' ? 'Jetzt registrieren' : 'Abgeschlossen',
        list: item.list,
        registeredTeams: item.registeredTeams,
      };
    });
  }

  /**
   * The units a countdown is spelled in, largest first.
   *
   * German needs two plural forms here: "Noch 19 Tage" is nominative, but
   * "Öffnet in 19 Tagen" is dative. The singular is the same in both.
   */
  private static readonly COUNTDOWN_UNITS: ReadonlyArray<{
    key: keyof CountdownParts;
    one: string;
    many: string;
    manyDative: string;
  }> = [
    { key: 'months', one: 'Monat', many: 'Monate', manyDative: 'Monaten' },
    { key: 'days', one: 'Tag', many: 'Tage', manyDative: 'Tagen' },
    { key: 'hours', one: 'Stunde', many: 'Stunden', manyDative: 'Stunden' },
    { key: 'minutes', one: 'Minute', many: 'Minuten', manyDative: 'Minuten' },
    {
      key: 'seconds',
      one: 'Sekunde',
      many: 'Sekunden',
      manyDative: 'Sekunden',
    },
  ];

  /** `date` moved by whole calendar months, clamped to the month's last day. */
  static addMonths(date: Date, count: number): Date {
    const dayOfMonth = date.getDate();
    const moved = new Date(date);
    // Move off the day-of-month first, so 31 January + 1 cannot land in March.
    moved.setDate(1);
    moved.setMonth(moved.getMonth() + count);
    const lastDay = new Date(
      moved.getFullYear(),
      moved.getMonth() + 1,
      0,
    ).getDate();
    moved.setDate(Math.min(dayOfMonth, lastDay));
    return moved;
  }

  /**
   * The gap between two moments, split into calendar units.
   *
   * Months are counted by actually walking the calendar rather than dividing
   * by thirty days, so the figure agrees with the two dates a reader can see
   * above it in the table.
   */
  static countdownParts(from: Date, to: Date): CountdownParts {
    const empty = { months: 0, days: 0, hours: 0, minutes: 0, seconds: 0 };
    if (to.getTime() <= from.getTime()) return empty;

    let months = 0;
    let cursor = from;
    for (;;) {
      const next = this.addMonths(from, months + 1);
      if (next.getTime() > to.getTime()) break;
      cursor = next;
      months += 1;
    }

    let rest = to.getTime() - cursor.getTime();
    const days = Math.floor(rest / 86_400_000);
    rest -= days * 86_400_000;
    const hours = Math.floor(rest / 3_600_000);
    rest -= hours * 3_600_000;
    const minutes = Math.floor(rest / 60_000);
    rest -= minutes * 60_000;

    return { months, days, hours, minutes, seconds: Math.floor(rest / 1000) };
  }

  /**
   * The gap as German text, carried by its two largest non-zero units —
   * "1 Monat und 21 Tage", then "3 Tage und 4 Stunden", down to "42 Sekunden",
   * and null once the gap is under a second.
   */
  static formatCountdown(
    from: Date,
    to: Date,
    grammaticalCase: 'nominative' | 'dative' = 'nominative',
  ): string | null {
    const parts = this.countdownParts(from, to);
    const filled = this.COUNTDOWN_UNITS.filter((unit) => parts[unit.key] > 0);
    // Under a second there is no unit to name; the caller phrases that itself.
    if (filled.length === 0) return null;

    return filled
      .slice(0, 2)
      .map((unit) => {
        const value = parts[unit.key];
        const plural =
          grammaticalCase === 'dative' ? unit.manyDative : unit.many;
        return `${value} ${value === 1 ? unit.one : plural}`;
      })
      .join(' und ');
  }

  /**
   * The moment a row is counting towards, and the state it is in at `now`.
   *
   * The backend's `registration_state` is where a row starts, and the browser
   * clock may only advance it, so a list left open across the opening moment
   * moves on by itself while a browser running behind the server never shows a
   * report as less advanced than the server says. The backend stays the
   * authority on what may actually be written — near a boundary a browser in
   * another timezone can be ahead of it, and then the refusal comes from the
   * API.
   */
  static registrationCountdown(
    row: RegistrationWindowFields,
    now: Date,
  ): { state: RegistrationState; target: Date | null } {
    const start = this.parseDottedDateToDate(row.date_start ?? '');
    const end =
      this.parseDottedDateToDate(row.date_end ?? '') ??
      this.parseDottedDateToDate(row.deadline);

    if (!end) return { state: this.registrationState(row), target: null };

    let fromClock: RegistrationState = 'open';
    if (end.getTime() <= now.getTime()) fromClock = 'closed';
    else if (start && start.getTime() > now.getTime())
      fromClock = 'not_yet_open';

    // The server's verdict is the floor: a browser whose clock or timezone is
    // behind the server must not show a report as less advanced than it is.
    // The clock is only allowed to move a row forward from there.
    const fromServer = row.registration_state;
    const rank: Record<RegistrationState, number> = {
      not_yet_open: 0,
      open: 1,
      closed: 2,
    };
    const state =
      fromServer && rank[fromServer] > rank[fromClock] ? fromServer : fromClock;

    if (state === 'closed') return { state, target: null };
    return { state, target: state === 'not_yet_open' ? start : end };
  }

  /** The line under the deadline: how long until it opens, until it closes, or that it is over. */
  static registrationHint(
    row: RegistrationWindowFields,
    now: Date = new Date(),
  ): string {
    const { state, target } = this.registrationCountdown(row, now);

    if (state === 'closed') return 'Frist abgelaufen';
    if (!target) {
      return state === 'not_yet_open' ? 'Noch nicht geöffnet' : '';
    }

    if (state === 'not_yet_open') {
      const gap = this.formatCountdown(now, target, 'dative');
      return gap ? `Öffnet in ${gap}` : 'Öffnet gleich';
    }

    const gap = this.formatCountdown(now, target);
    return gap ? `Noch ${gap}` : 'Weniger als 1 Sekunde';
  }

  /**
   * How often the list has to redraw for its countdowns to stay honest: every
   * second once a row shows seconds, every half minute while none does.
   */
  static countdownTickMs(
    rows: ReadonlyArray<RegistrationWindowFields>,
    now: Date,
  ): number {
    const showsSeconds = rows.some((row) => {
      const { target } = this.registrationCountdown(row, now);
      if (!target) return false;
      const parts = this.countdownParts(now, target);
      return parts.months === 0 && parts.days === 0 && parts.hours === 0;
    });

    return showsSeconds ? 1_000 : 30_000;
  }

  static async fetchReportList(archive?: boolean): Promise<TeamRow[]> {
    const url = archive ? 'report/list?archive=1' : 'report/list';
    const response = await axiosInstance.get(url);
    const items: ApiReportItem[] = Array.isArray(response.data)
      ? response.data
      : [];
    return this.mapReportItems(items);
  }

  static async fetchAssociationReportList(
    archive?: boolean,
  ): Promise<TeamRow[]> {
    const url = archive
      ? 'association/report/list?archive=1'
      : 'association/report/list';
    const response = await axiosInstance.get(url);
    const items: ApiReportItem[] = Array.isArray(response.data)
      ? response.data
      : [];
    return this.mapReportItems(items);
  }

  /**
   * Pulls the association Excel export of the open Meldungen ("Aktiv" tab), or
   * of one Jahresmeldung when reportId is given, and hands it to the browser.
   * The workbook is built on the fly, so this one request is allowed to take
   * longer than the default axios timeout.
   */
  static async downloadAssociationReportExcel(
    reportId?: number,
  ): Promise<void> {
    const response = await axiosInstance.get(
      reportId === undefined
        ? 'association/report/export/excel'
        : `association/report/${reportId}/export/excel`,
      {
        responseType: 'blob',
        timeout: 120000,
      },
    );

    const disposition = String(response.headers?.['content-disposition'] ?? '');
    const encoded = /filename\*=utf-8''([^;\n]+)/i.exec(disposition);
    const plain = /filename="?([^";\n]+)"?/i.exec(disposition);
    const fileName =
      (encoded ? decodeURIComponent(encoded[1]) : plain?.[1]) ||
      'Jahresmeldungen-Aktiv.xlsx';

    const blob = new Blob([response.data], {
      type:
        String(response.headers?.['content-type'] ?? '') ||
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    const objectUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = objectUrl;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(objectUrl);
  }

  static computeTotalPrice(
    list: ReportListItem[] = [],
    selectedCounts: Record<number, number | null>,
  ): number {
    return list.reduce((sum, item, index) => {
      const count = selectedCounts[index] ?? 0;
      return sum + item.price * (count || 0);
    }, 0);
  }

  static formatCurrencyDE(value: number): string {
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: 'EUR',
    }).format(value);
  }

  static computeTotalFromTeams(teams: Team[] = []): number {
    return teams.reduce(
      (sum, t) => sum + (t.selectedTeams ?? 0) * (t.price ?? 0),
      0,
    );
  }

  static computeTotalsFromTeams(teams: Team[] = []): {
    net: number;
    vat: number;
    gross: number;
  } {
    // Treat price as gross (VAT-included). Compute breakdown only.
    const totals = teams.reduce(
      (acc, team) => {
        const count = team.selectedTeams ?? 0;
        const pricePerTeamGross = Number(team.price ?? 0);
        const vatPercent = Number(team.vat ?? 0);
        const itemGross = count * pricePerTeamGross;
        let itemNet = itemGross;
        let itemVat = 0;
        if (vatPercent > 0) {
          itemNet = itemGross / (1 + vatPercent / 100);
          itemVat = itemGross - itemNet;
        }
        acc.gross += itemGross;
        acc.net += itemNet;
        acc.vat += itemVat;
        return acc;
      },
      { net: 0, vat: 0, gross: 0 },
    );
    return totals;
  }

  /** GVNB - Golf-Verband Niedersachsen-Bremen e.V. */
  static readonly SPIELGEMEINSCHAFT_ASSOCIATION_IDS = [2];

  static readonly SPIELGEMEINSCHAFT_CLUB_MAX_LENGTH = 250;

  /** A league is a Spielgemeinschaft when its label says so. */
  static isSpielgemeinschaftText(text?: string | null): boolean {
    if (!text) return false;
    return text.toLowerCase().includes('spielgemeinschaft');
  }

  /**
   * Only the associations that asked for it collect the club name. The backend
   * says so via `requiresSpielgemeinschaftClub`; the id list is the fallback for
   * a backend that has not been deployed yet.
   */
  static requiresSpielgemeinschaftClub(
    association?: AssociationInfo | null,
  ): boolean {
    if (!association) return false;
    if (typeof association.requiresSpielgemeinschaftClub === 'boolean') {
      return association.requiresSpielgemeinschaftClub;
    }
    return (
      typeof association.verbandId === 'number' &&
      this.SPIELGEMEINSCHAFT_ASSOCIATION_IDS.includes(association.verbandId)
    );
  }

  /**
   * Whether the "Spielgemeinschaft - Clubname" input belongs under this row:
   * the association collects it, the league is still open for reporting, its
   * label marks it as a Spielgemeinschaft and at least one team was reported.
   *
   * Wherever this is true the club name is mandatory, so the same check
   * decides both what is shown and what is enforced.
   */
  static needsSpielgemeinschaftClubInput(
    association: AssociationInfo | null | undefined,
    row: Pick<ReportListItem, 'text' | 'lock'> | undefined,
    selectedCount: number | null,
  ): boolean {
    if (!row || row.lock) return false;
    return (
      this.requiresSpielgemeinschaftClub(association) &&
      this.isSpielgemeinschaftText(row.text) &&
      (selectedCount ?? 0) > 0
    );
  }

  /**
   * The league labels whose club name is still missing. Empty means the
   * Spielgemeinschaft part of the form is complete and the meldung may go out.
   */
  static missingSpielgemeinschaftClubs(
    association: AssociationInfo | null | undefined,
    teams: Team[] = [],
  ): string[] {
    if (!this.requiresSpielgemeinschaftClub(association)) return [];

    return teams
      .filter((team) =>
        this.needsSpielgemeinschaftClubInput(
          association,
          team,
          team.selectedTeams,
        ),
      )
      .filter((team) => !(team.spielgemeinschaftClub || '').trim())
      .map((team) => team.text);
  }

  static parseGenderFromText(
    text?: string,
  ): 'male' | 'female' | 'boy' | 'girl' | 'unspecified' {
    if (!text) return 'unspecified';
    const normalized = text.toLowerCase();

    const hasDamen = /\bdamen\b/i.test(text) || normalized.includes('damen');
    const hasHerren = /\bherren\b/i.test(text) || normalized.includes('herren');
    const hasJungen = /\bjungen\b/i.test(text) || normalized.includes('jungen');
    // Support both "mädchen" and common ascii fallbacks "maedchen" / "madchen"
    const hasMaedchen =
      /\bm[äa]dchen\b/i.test(text) ||
      normalized.includes('mädchen') ||
      normalized.includes('maedchen') ||
      normalized.includes('madchen');

    const matches = [hasHerren, hasDamen, hasJungen, hasMaedchen].filter(
      Boolean,
    ).length;
    if (matches !== 1) return 'unspecified';

    if (hasHerren) return 'male';
    if (hasDamen) return 'female';
    if (hasJungen) return 'boy';
    if (hasMaedchen) return 'girl';
    return 'unspecified';
  }
}
